import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getBillInfoList, batchDeleteBill } from '@/api/bill'
import type { BillInfo, BillListParams } from '@/types/bill'
import { isApp } from '@/utils/bridge'
import { customStorage, getStorageItem } from '@/utils/storage'

/**
 * 账单 Store
 * 符合 Pinia 最佳实践：State + Getters + Actions
 */
export const useBillStore = defineStore('bill', () => {
  // ==================== State ====================

  // 账单列表
  const billList = ref<BillInfo[]>([])

  // 统计信息
  const totalCount = ref(0) // 账单总数
  const repaymentedAmount = ref(0) // 已还金额
  const unRepaymentAmount = ref(0) // 待还金额

  // 分页状态
  const currentPage = ref(1)
  const pageSize = ref(10)

  // 加载状态
  const loading = ref(false)

  // 滚动位置（用于恢复滚动状态）
  const scrollPosition = ref(0)

  // ==================== Getters（计算属性）====================

  /**
   * 是否有账单数据
   */
  const hasBills = computed(() => billList.value.length > 0)

  /**
   * 还款进度百分比
   */
  const repaymentPercentage = computed(() => {
    const total = repaymentedAmount.value + unRepaymentAmount.value
    if (total === 0) return 0
    return Math.round((repaymentedAmount.value / total) * 100)
  })

  /**
   * 总页数
   */
  const totalPages = computed(() => {
    if (pageSize.value === 0) return 0
    return Math.ceil(totalCount.value / pageSize.value)
  })

  /**
   * 是否还有更多数据
   */
  const hasMore = computed(() => {
    return currentPage.value < totalPages.value
  })

  // ==================== Actions（方法）====================

  /**
   * 获取账单列表
   * @param params 查询参数（可选，默认使用当前分页参数）
   */
  const fetchBillList = async (params?: Partial<BillListParams>) => {
    loading.value = true
    try {
      const requestParams: BillListParams = {
        current: params?.current ?? currentPage.value,
        size: params?.size ?? pageSize.value,
      }

      const data = await getBillInfoList(requestParams)

      if (data) {
        // 第一页替换数据，其他页追加数据
        if (requestParams.current === 1) {
          billList.value = data.records || []
        } else {
          billList.value = [...billList.value, ...(data.records || [])]
        }

        totalCount.value = data.total || 0
        repaymentedAmount.value = data.repaymentedAmount || 0
        unRepaymentAmount.value = data.unRepaymentAmount || 0
        currentPage.value = requestParams.current

        if (params?.size) {
          pageSize.value = params.size
        }
      }

      return data
    } catch (error) {
      console.error('获取账单列表失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * 刷新账单列表（重新加载第一页）
   */
  const refresh = async () => {
    currentPage.value = 1
    return await fetchBillList({ current: 1 })
  }

  const clearAllBillData = () => {
    // 清理所有与账单相关的状态，供注销或销号时调用
    billList.value = []
    totalCount.value = 0
    repaymentedAmount.value = 0
    unRepaymentAmount.value = 0
    currentPage.value = 1
    pageSize.value = 10
    loading.value = false
    scrollPosition.value = 0
    // 复位分页/加载状态
    // history flags not relevant here
  }

  /**
   * 加载更多（加载下一页）
   */
  const loadMore = async () => {
    if (!hasMore.value || loading.value) {
      return
    }
    currentPage.value++
    return await fetchBillList({ current: currentPage.value })
  }

  /**
   * 根据 ID 获取单个账单
   * @param id 账单 ID
   */
  const getBillById = (id: string | number): BillInfo | undefined => {
    return billList.value.find((bill) => bill.id === id)
  }

  /**
   * 设置每页大小
   * @param size 每页条数
   */
  const setPageSize = (size: number) => {
    if (size > 0) {
      pageSize.value = size
      currentPage.value = 1
    }
  }

  /**
   * 清空账单数据
   */
  const clearBillData = () => {
    billList.value = []
    totalCount.value = 0
    repaymentedAmount.value = 0
    unRepaymentAmount.value = 0
    currentPage.value = 1
    scrollPosition.value = 0
  }

  /**
   * 保存滚动位置
   */
  const saveScrollPosition = (position: number) => {
    scrollPosition.value = position
  }

  /**
   * 删除账单
   * @param billId 账单ID
   */
  const deleteBill = async (billId: string) => {
    try {
      await batchDeleteBill([billId])

      // 删除成功，从本地列表中移除
      billList.value = billList.value.filter(bill => bill.id !== billId)
      totalCount.value = Math.max(0, totalCount.value - 1)

      // 刷新列表获取最新的统计数据
      await refresh()

      return true
    } catch (error) {
      console.error('删除账单失败:', error)
      throw error
    }
  }

  return {
    // State
    billList,
    totalCount,
    repaymentedAmount,
    unRepaymentAmount,
    loading,
    currentPage,
    pageSize,
    scrollPosition,

    // Getters
    hasBills,
    repaymentPercentage,
    totalPages,
    hasMore,

    // Actions
    fetchBillList,
    refresh,
    loadMore,
    getBillById,
    setPageSize,
    clearBillData,
    clearAllBillData,
    saveScrollPosition,
    deleteBill,
  }
},
  // 持久化配置（仅 App 环境启用）
  // H5 是 SPA，store 数据在内存中，页面跳转不会丢失，无需持久化
  // App 每个页面是独立 WebView，store 数据不共享，需要持久化到 Native
  isApp() ? {
    persist: {
      key: 'bill-store',
      storage: customStorage,
      // 只持久化滚动位置，用于恢复滚动状态
      // 列表数据不持久化（太大且需要重新获取最新数据）
      paths: ['scrollPosition'],
      async beforeHydrate(ctx) {
        try {
          const savedState = await getStorageItem('bill-store')
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollPosition !== undefined) ctx.store.scrollPosition = state.scrollPosition
          }
        } catch (error) {
          console.error('从 Native 加载 billStore 数据失败:', error)
        }
      },
    },
  } : {}
)
